import { useState, useEffect, useRef } from 'react';
// import { withStyles } from '@mui/styles';
import { styled } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import { QuoteSubmissionProgress } from '../../../common/QuoteSubmissionProgress/QuoteSubmissionProgress';
import DialogTitle from '@mui/material/DialogContent';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

// connecting to the state for submitting the partially completed quote
import { useSelector, useDispatch } from 'react-redux';

// for the dialog form
import { Form, FormApi } from 'informed';
import { EmailInput, TextField, TextArea } from '@calefy-inc/informedMaterial';

// types and classes
import { useSubmitPolicyStreamQuote } from '../../../../hooks';
import {
  getIncompleteQuoteSubmissionError,
  getLastSubmittedQuote,
  getSaveButtonVisibility,
  getQuoteSubmissionLoading,
  setResumeTo,
  policystreamStore,
} from '../../../../store/policystreamStore';
import { navigate } from '@reach/router';
import { policyStreamPaths } from '../../policyStreamConstants';
import { safelyGetValues } from '../../../../util';
import { FormErrorsWarning } from '../../../QuoteWizard/FormErrorsWarning';
import { usePolicyStreamQuoteSubmission } from '../../../../hooks/usePolicyStreamQuoteSubmission';
import { errorify } from '../../../../util';
import { createApplicationSavedThunk } from '../../../../store/analyticsStore';
import {
  CoreTaskStatusChoices,
  GetTaskPoolQuery,
} from '../../../../gql/graphql';

interface SaveParams {
  clientName?: string;
  clientEmail: string;
  message?: string;
}

/**
 * Button to save the current quote to the backend in a partially completed state in such a way that someone else can come along and complete it later
 * @param classes - theming classes
 * @param reconcileState - Function which should be called to reconcile the current state of the store with whatever information is currently entered into the tab on which this button is rendered. This should only deal with that portion of the state - appropriate actions to actually save the state will be taken elsewhere in the component
 */
export const PolicyStreamSaveQuoteButton = () => {
  const submitQuote = useSubmitPolicyStreamQuote();
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const visible = useSelector(getSaveButtonVisibility);
  const dispatch = useDispatch();

  return visible ? (
    <>
      <Tooltip
        enterDelay={100}
        leaveDelay={100}
        title='Use this to save forms for later completion or sending to a trusted party'
      >
        <Button
          onClick={() => {
            // reconcileState();
            setDialogOpen(true);
          }}
          variant='outlined'
          startIcon={<SaveIcon />}
        >
          Save For Later
        </Button>
      </Tooltip>
      <EmailDialog
        open={dialogOpen}
        setOpen={(b) => setDialogOpen(b)}
        handleClose={() => {
          setDialogOpen(false);
        }}
        onOk={({ clientName, clientEmail, message }) => {
          const pathParts = window.location.pathname.split('/').filter(Boolean);
          const lastFolder = pathParts[pathParts.length - 1] || '';
          dispatch(setResumeTo({ resumeTo: lastFolder }));

          submitQuote({
            status: 'INCOMPLETE',
            clientName,
            clientEmail,
            message,
          });
        }}
      />
    </>
  ) : null;
};

interface EmailDialogProps {
  open: boolean;
  setOpen: (b: boolean) => void;
  handleClose: () => void;
  onOk: (params: SaveParams) => void;
}
/**
 * Dialog to ask the user for an email to send the partially completed form to
 */
const EmailDialog = ({
  open,
  setOpen,
  handleClose,
  onOk,
}: // classes = {},
EmailDialogProps) => {
  // whether the email is valid -> we can use the OK button
  const [pending, setPending] = useState<boolean>(false);
  const formApiRef = useRef<FormApi>();
  const dispatch = useDispatch();
  const submissionError = useSelector(getIncompleteQuoteSubmissionError);
  const submittedQuoteInStore = useSelector(getLastSubmittedQuote);
  const isQuoteSubmissionLoading = useSelector(getQuoteSubmissionLoading);
  const [formError, setFormError] = useState<string | undefined>();

  const { taskPool, quote } = usePolicyStreamQuoteSubmission({
    active: open,
    onComplete: () => {
      dispatch(createApplicationSavedThunk());
    },
  });

  // if we are dealing with an error, set the pending status to false (there's a separate dialog which sorts that out)
  useEffect(() => {
    if (submissionError) {
      setPending(false);
    }
  }, [submissionError]);

  // if there's a submitted quote then we're done - navigate to the done page
  useEffect(() => {
    if (
      submittedQuoteInStore &&
      submittedQuoteInStore.status === 'INCOMPLETE'
    ) {
      setPending(false);
      setOpen(false);
      navigate(policyStreamPaths.done.url);
    }
  }, [submittedQuoteInStore, setOpen]);

  // Update errors in state when there are errors from quote submission
  useEffect(() => {
    const taskPoolData = taskPool.data?.getTaskPool;
    const error = taskPool.error || taskPoolData?.error || quote.error;

    if (error) {
      dispatch(
        policystreamStore.actions.updateErrors({
          incompleteQuoteSubmission: errorify(error),
        }),
      );
    }
  }, [
    taskPool.error,
    taskPool.data?.getTaskPool?.error,
    quote.error,
    dispatch,
  ]);

  // Calculate progress percentage and check if task pool is complete
  const taskPoolData = taskPool.data?.getTaskPool;
  const hasTaskPoolErrors = !!taskPool.error || !!taskPoolData?.error;
  const hasQuoteErrors = !!quote.error;

  const progressPercentage = taskPoolData
    ? getProgressPercentage(taskPoolData)
    : 0;
  const isTaskPoolComplete = progressPercentage === 100;

  // Determine loading state: loading if queries are loading OR task pool is not complete and no errors
  const isLoading =
    taskPool.loading ||
    quote.loading ||
    (taskPoolData &&
      !isTaskPoolComplete &&
      !hasTaskPoolErrors &&
      !hasQuoteErrors);
  const shouldShowProgress =
    taskPoolData && !hasTaskPoolErrors && !hasQuoteErrors;

  return (
    <>
      <StyledDialog
        open={open}
        onClose={handleClose}
        aria-labelledby='form-dialog-title'
        className={'' /* classes.formModal */}
      >
        <DialogTitle>Save and Send Application</DialogTitle>
        <div className={'' /* classes.formModalContents */}>
          <DialogContent>
            <Typography
              variant='body2'
              className={'' /* classes.instructionText */}
            >
              Please enter your email below if you wish to save and complete
              your application at a later time.
            </Typography>
            <Form
              initialValues={
                // TODO actually try to get these values from the form
                { clientName: '', email: '', message: '' }
              }
              formApiRef={formApiRef}
              onSubmit={({ values }) => {
                const {
                  clientName,
                  email: clientEmail,
                  message,
                } = safelyGetValues(values, ['clientName', 'email', 'message']);
                if (!clientEmail) {
                  setFormError(`Email is required.`);
                  return;
                }
                setFormError(undefined);
                setPending(true);
                onOk({ clientName, clientEmail, message });
              }}
              className={'' /* classes.form */}
            >
              {() => {
                return (
                  <>
                    <TextField
                      name='clientName'
                      label='(Optional) Enter recipient name'
                      fullWidth
                      className={'' /* classes.textField */}
                      variant='standard'
                    />
                    <EmailInput
                      name='email'
                      label='Enter email'
                      required={true}
                      fullWidth
                      validateOnChange={true}
                      className={'' /* classes.textField */}
                    />
                    <TextArea
                      name='message'
                      label='(Optional) Enter a short message for the recipient'
                      fullWidth
                      className={'' /* classes.textarea */}
                    />
                    {isQuoteSubmissionLoading && !pending ? (
                      <p>A submission is in progress, please wait</p>
                    ) : null}
                    <QuoteSubmissionProgress
                      progressPercentage={progressPercentage}
                      show={!!shouldShowProgress}
                    />
                    <FormErrorsWarning error={formError} />
                    <div className={'' /* classes.buttonGroup */}>
                      <StyledDialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button
                          disabled={
                            !formApiRef.current ||
                            pending ||
                            isLoading ||
                            isQuoteSubmissionLoading
                          }
                          type='submit'
                          variant='contained'
                        >
                          {!pending && !isLoading
                            ? 'OK'
                            : isLoading
                            ? 'Processing...'
                            : 'Saving...'}
                        </Button>
                      </StyledDialogActions>
                    </div>
                  </>
                );
              }}
            </Form>
          </DialogContent>
        </div>
        {/* <IncompleteQuoteSubmissionError /> */}
      </StyledDialog>
    </>
  );
};

const StyledDialogActions = styled(DialogActions)(({ theme }) => {
  return {
    marginTop: theme.spacing(3),
  };
});

const StyledDialog = styled(Dialog)({
  display: 'flex',
  margin: 'auto',
  justifyContent: 'center',
  alignItems: 'center',
});

// ----------------------------------------------------------------------
// Utility Functions
// ----------------------------------------------------------------------

function getProgressPercentage(
  taskPool: NonNullable<GetTaskPoolQuery['getTaskPool']> | null | undefined,
): number {
  if (!taskPool?.tasks || taskPool.tasks.length === 0) {
    return 0;
  }

  const totalTasks = taskPool.tasks.length;
  // Treat FAILED as "finished" so optional task failures still advance progress.
  const finishedTasks = taskPool.tasks.filter(
    (task) =>
      task?.status === CoreTaskStatusChoices.Completed ||
      task?.status === CoreTaskStatusChoices.Failed,
  ).length;

  return (finishedTasks / totalTasks) * 100;
}
